/**
 * Content parsing utilities for MarthaEditor
 * Extracted from BlockNoteEditor.tsx — handles markdown/JSON detection,
 * sanitization, fast parsing for large documents, and format conversion.
 */

import type { BlockNoteEditor as BlockNoteEditorCore } from '@blocknote/core';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PartialBlockType = any;

// Size threshold (in characters) above which we use the fast parser instead of
// BlockNote's tryParseMarkdownToBlocks.  The built-in parser converts markdown
// → HTML → ProseMirror DOM nodes synchronously on the main thread.  For docs
// with complex tables (especially containing code with `|` pipe chars),
// tryParseMarkdownToBlocks can produce malformed table structures that trigger
// infinite React re-renders (error #185 — maximum update depth exceeded).
// 5KB is deliberately low: the fast parser handles tables, code blocks, and
// inline styles correctly without the DOM round-trip.
export const FAST_PARSE_THRESHOLD = 5_000; // 5KB

/**
 * Detect if content is BlockNote JSON (array of block objects)
 */
export function isBlockNoteJSON(content: string): boolean {
  const trimmed = content.trim();
  if (!trimmed.startsWith('[')) return false;
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object' && 'type' in parsed[0];
  } catch {
    return false;
  }
}

/**
 * Sanitize markdown content before parsing.
 * Strips junk lines generated by blocksToMarkdownLossy():
 * - `> \` lines (empty blockquote paragraphs)
 * - standalone `\` lines (empty paragraph markers)
 * - runs of 3+ consecutive empty lines → collapsed to 2
 * These accumulate over save/load cycles and can bloat documents 100x+.
 */
export function sanitizeMarkdown(content: string): string {
  return content
    .replace(/^> *\\+\s*$/gm, '') // Remove `> \` junk lines
    .replace(/^\\+\s*$/gm, '')     // Remove standalone `\` lines
    .replace(/\n{3,}/g, '\n\n');   // Collapse excessive blank lines
}

/**
 * Fast markdown-to-blocks parser for large documents.
 * Converts markdown line-by-line to basic BlockNote block types without
 * using the slow tryParseMarkdownToBlocks() which blocks the main thread.
 *
 * Supports: headings (h1-h3), paragraphs, bullet lists, numbered lists,
 * code blocks, blockquotes, horizontal rules, tables, bold/italic inline styles.
 */
export function fastParseMarkdown(content: string): PartialBlockType[] {
  const lines = content.split('\n');
  const blocks: PartialBlockType[] = [];
  let i = 0;

  function parseInline(text: string): PartialBlockType[] {
    const result: PartialBlockType[] = [];
    const regex = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\[([^\]]+)\]\(([^)]+)\))/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        result.push({ type: 'text', text: text.slice(lastIndex, match.index) } as PartialBlockType);
      }

      if (match[2]) {
        result.push({ type: 'text', text: match[2], styles: { bold: true, italic: true } } as PartialBlockType);
      } else if (match[3]) {
        result.push({ type: 'text', text: match[3], styles: { bold: true } } as PartialBlockType);
      } else if (match[4]) {
        result.push({ type: 'text', text: match[4], styles: { italic: true } } as PartialBlockType);
      } else if (match[5]) {
        result.push({ type: 'text', text: match[5], styles: { code: true } } as PartialBlockType);
      } else if (match[6] && match[7]) {
        result.push({ type: 'link', href: match[7], content: [{ type: 'text', text: match[6] }] } as PartialBlockType);
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      result.push({ type: 'text', text: text.slice(lastIndex) } as PartialBlockType);
    }

    return result.length > 0 ? result : [{ type: 'text', text } as PartialBlockType];
  }

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i++;
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line.trim())) {
      blocks.push({ type: 'paragraph', content: [{ type: 'text', text: '---' }] });
      i++;
      continue;
    }

    // Headings
    const headingMatch = line.match(/^(#{1,3})\s+(.+)/);
    if (headingMatch) {
      const level = headingMatch[1].length as 1 | 2 | 3;
      blocks.push({
        type: 'heading',
        props: { level },
        content: parseInline(headingMatch[2].trim()),
      });
      i++;
      continue;
    }

    // Code blocks
    if (line.trim().startsWith('```')) {
      const lang = line.trim().slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```

      // Detect mermaid code blocks and convert to mermaidDiagram block type
      if (lang === 'mermaid') {
        blocks.push({
          type: 'mermaidDiagram',
          props: { code: codeLines.join('\n') },
        });
      } else {
        blocks.push({
          type: 'codeBlock',
          props: { language: lang || 'text' },
          content: [{ type: 'text', text: codeLines.join('\n') }],
        });
      }
      continue;
    }

    // Blockquotes
    if (line.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && (lines[i].startsWith('>') || (lines[i].trim() !== '' && quoteLines.length > 0 && !lines[i].startsWith('#')))) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({
        type: 'paragraph',
        content: parseInline(quoteLines.join(' ').trim()),
      });
      continue;
    }

    // Bullet lists
    if (/^\s*[-*+]\s/.test(line)) {
      while (i < lines.length && /^\s*[-*+]\s/.test(lines[i])) {
        const text = lines[i].replace(/^\s*[-*+]\s+/, '');
        blocks.push({
          type: 'bulletListItem',
          content: parseInline(text.trim()),
        });
        i++;
      }
      continue;
    }

    // Numbered lists
    if (/^\s*\d+[.)]\s/.test(line)) {
      while (i < lines.length && /^\s*\d+[.)]\s/.test(lines[i])) {
        const text = lines[i].replace(/^\s*\d+[.)]\s+/, '');
        blocks.push({
          type: 'numberedListItem',
          content: parseInline(text.trim()),
        });
        i++;
      }
      continue;
    }

    // Tables
    if (line.trim().startsWith('|') && line.includes('|', 1)) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].includes('|', 1)) {
        tableLines.push(lines[i]);
        i++;
      }

      if (tableLines.length >= 2) {
        const rows: { cells: PartialBlockType[][] }[] = [];
        let headerRowCount = 0;

        for (let r = 0; r < tableLines.length; r++) {
          const rowText = tableLines[r].trim();

          if (/^\|[\s\-:+|]+\|?\s*$/.test(rowText) && rowText.includes('-')) {
            headerRowCount = r;
            continue;
          }

          const parts = rowText.split('|');
          const cellTexts = parts.slice(1, parts.length - (parts[parts.length - 1].trim() === '' ? 1 : 0))
            .map(c => c.trim());

          const cells = cellTexts.map(text => parseInline(text));
          rows.push({ cells });
        }

        if (rows.length > 0) {
          // Normalize: ensure every row has the same number of cells
          const colCount = Math.max(...rows.map(r => r.cells.length));
          for (const row of rows) {
            while (row.cells.length < colCount) {
              row.cells.push([{ type: 'text', text: '' }]);
            }
          }
          blocks.push({
            type: 'table',
            content: {
              type: 'tableContent',
              rows,
            },
          });
        }
      }
      continue;
    }

    // Regular paragraph
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].match(/^#{1,3}\s/) &&
      !lines[i].trim().startsWith('```') &&
      !lines[i].startsWith('>') &&
      !/^\s*[-*+]\s/.test(lines[i]) &&
      !/^\s*\d+[.)]\s/.test(lines[i]) &&
      !/^(-{3,}|\*{3,}|_{3,})\s*$/.test(lines[i].trim()) &&
      !(lines[i].trim().startsWith('|') && lines[i].includes('|', 1))
    ) {
      paraLines.push(lines[i]);
      i++;
    }

    if (paraLines.length > 0) {
      blocks.push({
        type: 'paragraph',
        content: parseInline(paraLines.join(' ').trim()),
      });
    }
  }

  return blocks;
}

// ─── HTML entity decoding ──────────────────────────────────────────────────────

/**
 * Common HTML entities found in document content.
 * Only includes entities actually encountered in stored documents.
 */
const HTML_ENTITY_MAP: Record<string, string> = {
  '&sect;': '§',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&nbsp;': '\u00A0',
  '&ndash;': '–',
  '&mdash;': '—',
  '&lsquo;': '\u2018',
  '&rsquo;': '\u2019',
  '&ldquo;': '\u201C',
  '&rdquo;': '\u201D',
  '&hellip;': '…',
  '&bull;': '•',
  '&times;': '×',
  '&divide;': '÷',
  '&copy;': '©',
  '&reg;': '®',
  '&trade;': '™',
  '&deg;': '°',
  '&plusmn;': '±',
  '&frac12;': '½',
  '&frac14;': '¼',
  '&frac34;': '¾',
};

const HTML_ENTITY_REGEX = /&(?:#(\d+)|#x([0-9a-fA-F]+)|([a-zA-Z]+));/g;

function decodeHTMLEntities(text: string): string {
  if (!text.includes('&')) return text;
  return text.replace(HTML_ENTITY_REGEX, (match, decimal, hex, named) => {
    if (decimal) return String.fromCodePoint(parseInt(decimal, 10));
    if (hex) return String.fromCodePoint(parseInt(hex, 16));
    if (named) return HTML_ENTITY_MAP[`&${named};`] ?? match;
    return match;
  });
}

/**
 * Decode entities in an inline content item (text node or link with nested content).
 */
function decodeInlineItem(item: PartialBlockType): PartialBlockType {
  if (!item || typeof item !== 'object') return item;
  if (item.type === 'text' && typeof item.text === 'string') {
    const decoded = decodeHTMLEntities(item.text);
    return decoded !== item.text ? { ...item, text: decoded } : item;
  }
  // Recurse into link/inline content
  if (Array.isArray(item.content)) {
    const newContent = item.content.map((c: PartialBlockType) => decodeInlineItem(c));
    return { ...item, content: newContent };
  }
  return item;
}

/**
 * Recursively decode HTML entities in all text content within blocks.
 * Handles nested content arrays (e.g. inline content, table cells).
 */
export function decodeBlockEntities(blocks: PartialBlockType[]): PartialBlockType[] {
  if (!Array.isArray(blocks)) return blocks;
  return blocks.map((block: PartialBlockType) => {
    if (!block || typeof block !== 'object') return block;

    const result = { ...block };

    // Decode text in content arrays (inline content)
    if (Array.isArray(result.content)) {
      result.content = result.content.map(decodeInlineItem);
    }

    // Handle table content (rows → cells → inline content)
    if (
      result.content &&
      typeof result.content === 'object' &&
      !Array.isArray(result.content) &&
      result.content.type === 'tableContent' &&
      Array.isArray(result.content.rows)
    ) {
      result.content = {
        ...result.content,
        rows: result.content.rows.map((row: PartialBlockType) => {
          if (!row?.cells || !Array.isArray(row.cells)) return row;
          return {
            ...row,
            cells: row.cells.map((cell: PartialBlockType) =>
              Array.isArray(cell) ? cell.map(decodeInlineItem) : cell
            ),
          };
        }),
      };
    }

    // Decode text in props (e.g. mermaidDiagram code prop)
    if (result.props && typeof result.props === 'object') {
      const newProps = { ...result.props };
      let propsChanged = false;
      for (const key of Object.keys(newProps)) {
        if (typeof newProps[key] === 'string' && newProps[key].includes('&')) {
          const decoded = decodeHTMLEntities(newProps[key]);
          if (decoded !== newProps[key]) {
            newProps[key] = decoded;
            propsChanged = true;
          }
        }
      }
      if (propsChanged) result.props = newProps;
    }

    // Recurse into children blocks
    if (Array.isArray(result.children) && result.children.length > 0) {
      result.children = decodeBlockEntities(result.children);
    }

    return result;
  });
}

// ─── ASCII Art to Mermaid migration ──────────────────────────────────────────

/**
 * Detect box-drawing characters (Unicode Box Drawing block U+2500–U+257F).
 */
const BOX_DRAWING_REGEX = /[\u2500-\u257F]/;

function hasBoxDrawingCharacters(text: string): boolean {
  return BOX_DRAWING_REGEX.test(text);
}

/**
 * Represents a parsed ASCII art box.
 */
interface AsciiBox {
  row: number;     // top-left row in the character grid
  col: number;     // top-left column
  width: number;   // character width including borders
  height: number;  // character height including borders
  label: string;   // first meaningful text line
  details: string; // remaining text lines joined
}

/**
 * Represents a connection between two ASCII boxes.
 */
interface AsciiConnection {
  fromIdx: number;
  toIdx: number;
  label: string;
}

/**
 * Extract rectangular boxes from ASCII art text.
 * Finds boxes bounded by ┌─┐│└─┘ corner/edge characters.
 */
function extractAsciiBoxes(text: string): AsciiBox[] {
  const lines = text.split('\n');
  const boxes: AsciiBox[] = [];
  const visited = new Set<string>();

  for (let r = 0; r < lines.length; r++) {
    for (let c = 0; c < lines[r].length; c++) {
      if (lines[r][c] !== '┌' && lines[r][c] !== '╔') continue;
      const key = `${r},${c}`;
      if (visited.has(key)) continue;

      // Trace right to find ┐ or ╗
      let rightEnd = -1;
      for (let cc = c + 1; cc < lines[r].length; cc++) {
        const ch = lines[r][cc];
        if (ch === '┐' || ch === '╗') { rightEnd = cc; break; }
        if (ch !== '─' && ch !== '═' && ch !== '┬' && ch !== '┴' && ch !== '╤' && ch !== '╧') break;
      }
      if (rightEnd < 0) continue;

      // Trace down to find └ or ╚ (allow ±1 column tolerance for misaligned ASCII art)
      let bottomEnd = -1;
      let bottomCol = c; // actual column where └ was found
      for (let rr = r + 1; rr < lines.length; rr++) {
        const line = lines[rr] || '';
        // Check exact column and ±1 for └
        for (const cc of [c, c - 1, c + 1]) {
          if (cc >= 0 && cc < line.length && (line[cc] === '└' || line[cc] === '╚')) {
            bottomEnd = rr;
            bottomCol = cc;
            break;
          }
        }
        if (bottomEnd >= 0) break;
        // Continue if current col has a vertical border
        if (c >= line.length) break;
        const ch = line[c];
        if (ch !== '│' && ch !== '║' && ch !== '├' && ch !== '┤' && ch !== '╠' && ch !== '╣' && ch !== ' ') break;
      }
      if (bottomEnd < 0) continue;

      // Verify bottom-right corner exists (allow ±1 tolerance)
      const bottomLine = lines[bottomEnd] || '';
      let brFound = false;
      for (const offset of [0, -1, 1]) {
        const checkCol = rightEnd + offset;
        if (checkCol >= 0 && checkCol < bottomLine.length) {
          const ch = bottomLine[checkCol];
          if (ch === '┘' || ch === '╝') { brFound = true; break; }
        }
      }
      if (!brFound) continue;

      const width = rightEnd - c + 1;
      const height = bottomEnd - r + 1;

      // Extract text inside the box (between borders)
      const textLines: string[] = [];
      for (let rr = r + 1; rr < bottomEnd; rr++) {
        const line = lines[rr] || '';
        // Get text between left and right borders
        const inner = line.substring(c + 1, Math.min(rightEnd, line.length));
        // Strip border characters from edges
        const cleaned = inner.replace(/^[│║]\s*/, '').replace(/\s*[│║]$/, '').trim();
        if (cleaned) textLines.push(cleaned);
      }

      const label = textLines[0] || `Box ${boxes.length + 1}`;
      const details = textLines.slice(1).join('\n');

      visited.add(key);
      boxes.push({ row: r, col: c, width, height, label, details });
    }
  }

  return boxes;
}

/**
 * Detect connections between boxes by looking for arrows (│ ▲ ▼ ┴ ├ ┤)
 * in the space between boxes.
 */
function detectConnections(text: string, boxes: AsciiBox[]): AsciiConnection[] {
  const lines = text.split('\n');
  const connections: AsciiConnection[] = [];

  if (boxes.length < 2) return connections;

  // For each pair of boxes, check if there's a vertical or horizontal connector
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      const a = boxes[i];
      const b = boxes[j];

      // Check vertical connection (a above b)
      if (a.row + a.height <= b.row) {
        // Look for │ or ▲ or ▼ characters between a's bottom and b's top
        // Use the column range of box b (the lower box) to scope the search
        const colOverlapStart = Math.max(a.col, b.col);
        const colOverlapEnd = Math.min(a.col + a.width, b.col + b.width);
        if (colOverlapStart < colOverlapEnd) {
          let hasConnection = false;
          let connectionLabel = '';
          for (let rr = a.row + a.height; rr < b.row; rr++) {
            const line = lines[rr] || '';
            for (let cc = colOverlapStart; cc < colOverlapEnd; cc++) {
              const ch = line[cc];
              if (ch === '│' || ch === '▲' || ch === '▼' || ch === '║' || ch === '┴' || ch === '┬') {
                hasConnection = true;
              }
            }
            // Extract label text ONLY from the column range of box b (not the whole line)
            const segment = line.substring(b.col, b.col + b.width);
            if (segment) {
              const labelCandidate = segment.replace(/[│║▲▼┴┬┌┐└┘─═\s]+/g, ' ').trim();
              if (labelCandidate && labelCandidate.length > 1 && !connectionLabel) {
                connectionLabel = labelCandidate;
              }
            }
          }
          if (hasConnection) {
            connections.push({ fromIdx: j, toIdx: i, label: connectionLabel });
          }
        }
      }

      // Check horizontal connection (a left of b)
      if (a.col + a.width <= b.col && Math.abs(a.row - b.row) < Math.max(a.height, b.height)) {
        let hasConnection = false;
        const rowOverlapStart = Math.max(a.row, b.row);
        const rowOverlapEnd = Math.min(a.row + a.height, b.row + b.height);
        for (let rr = rowOverlapStart; rr < rowOverlapEnd; rr++) {
          const line = lines[rr] || '';
          for (let cc = a.col + a.width; cc < b.col; cc++) {
            const ch = line[cc];
            if (ch === '─' || ch === '═' || ch === '►' || ch === '◄' || ch === '→' || ch === '←') {
              hasConnection = true;
            }
          }
        }
        if (hasConnection) {
          connections.push({ fromIdx: i, toIdx: j, label: '' });
        }
      }
    }
  }

  return connections;
}

/**
 * Convert ASCII art architecture diagram to Mermaid flowchart code.
 * Returns null if the text doesn't contain a valid multi-box diagram.
 */
export function asciiArtToMermaid(text: string): string | null {
  const boxes = extractAsciiBoxes(text);
  if (boxes.length < 2) return null;

  const connections = detectConnections(text, boxes);

  // Determine layout direction based on box positions
  const topRow = Math.min(...boxes.map(b => b.row));
  const bottomRow = Math.max(...boxes.map(b => b.row));
  const leftCol = Math.min(...boxes.map(b => b.col));
  const rightCol = Math.max(...boxes.map(b => b.col));
  const isVertical = (bottomRow - topRow) > (rightCol - leftCol) / 2;
  const direction = isVertical ? 'TB' : 'LR';

  let mermaid = `graph ${direction}\n`;

  // Helper: escape for Mermaid labels
  const esc = (s: string) => s.replace(/"/g, '#quot;');

  // Generate nodes — title bold, limited detail lines for clean rendering
  boxes.forEach((box, i) => {
    const nodeId = `N${i}`;
    const title = esc(box.label);
    const detailLines = box.details
      .split('\n')
      .filter(l => l.trim())
      .slice(0, 3) // Max 3 detail lines for readability
      .map(l => esc(l.trim()));

    if (detailLines.length > 0) {
      const body = detailLines.join('<br/>');
      mermaid += `  ${nodeId}["<b>${title}</b><br/><small>${body}</small>"]\n`;
    } else {
      mermaid += `  ${nodeId}["${title}"]\n`;
    }
  });

  // Generate connections — preserve directional semantics
  if (connections.length > 0) {
    connections.forEach(({ fromIdx, toIdx, label }) => {
      if (label) {
        mermaid += `  N${fromIdx} -->|"${esc(label)}"| N${toIdx}\n`;
      } else {
        mermaid += `  N${fromIdx} --> N${toIdx}\n`;
      }
    });
  } else {
    // No explicit connections found — infer from spatial arrangement
    const sorted = boxes.map((b, i) => ({ ...b, idx: i })).sort((a, b) => a.row - b.row || a.col - b.col);
    for (let i = 1; i < sorted.length; i++) {
      mermaid += `  N${sorted[i - 1].idx} --> N${sorted[i].idx}\n`;
    }
  }

  return mermaid;
}

/**
 * Migrate codeBlocks containing ASCII art box-drawing diagrams
 * to mermaidDiagram blocks for beautiful rendering.
 */
export function migrateAsciiArtBlocks(blocks: PartialBlockType[]): PartialBlockType[] {
  return blocks.map(block => {
    if (block.type === 'codeBlock' && Array.isArray(block.content)) {
      const text = block.content
        .filter((c: { type: string }) => c.type === 'text')
        .map((c: { text?: string }) => c.text || '')
        .join('');
      if (hasBoxDrawingCharacters(text)) {
        const mermaidCode = asciiArtToMermaid(text);
        if (mermaidCode) {
          return { type: 'mermaidDiagram', props: { code: mermaidCode } };
        }
      }
    }
    return block;
  });
}

/**
 * Migrate mermaid code blocks to dedicated MermaidDiagram block type.
 * Runs once during load to convert existing content.
 */
export function migrateMermaidBlocks(blocks: PartialBlockType[]): PartialBlockType[] {
  return blocks.map(block => {
    if (
      block.type === 'codeBlock' &&
      block.props?.language === 'mermaid' &&
      Array.isArray(block.content)
    ) {
      const code = block.content
        .filter((c: { type: string }) => c.type === 'text')
        .map((c: { text?: string }) => c.text || '')
        .join('');
      if (code.trim()) {
        return { type: 'mermaidDiagram', props: { code } };
      }
    }
    return block;
  });
}

/**
 * Detect and unwrap double-encoded blocks: a small number of paragraph blocks
 * whose concatenated text content is itself valid BlockNote JSON.
 * This occurs when blocks were JSON-stringified and saved as text within paragraphs.
 * Recurses up to 5 levels deep (triple+ encoding has been observed in production).
 */
function unwrapDoubleEncodedBlocks(blocks: PartialBlockType[]): PartialBlockType[] {
  let current = blocks;

  for (let depth = 0; depth < 5; depth++) {
    // Only check if we have few blocks (heuristic for wrapped content)
    if (current.length > 5) return current;

    const paragraphBlocks = current.filter(
      (b: PartialBlockType) => b.type === 'paragraph' && Array.isArray(b.content)
    );
    if (paragraphBlocks.length === 0 || paragraphBlocks.length !== current.length) return current;

    // Concatenate all text content from all paragraph blocks
    const allText = paragraphBlocks
      .map((b: PartialBlockType) =>
        b.content
          .filter((c: PartialBlockType) => c.type === 'text')
          .map((c: PartialBlockType) => c.text || '')
          .join('')
      )
      .join('');

    // Check if the concatenated text is itself valid BlockNote JSON
    const trimmed = allText.trim();
    if (!trimmed.startsWith('[') || trimmed.length < 50) return current;

    try {
      const innerBlocks = JSON.parse(trimmed);
      if (
        Array.isArray(innerBlocks) &&
        innerBlocks.length > 0 &&
        typeof innerBlocks[0] === 'object' &&
        'type' in innerBlocks[0]
      ) {
        console.log(
          `[MarthaEditor] Unwrapped encoded content (depth ${depth + 1}): ${current.length} wrapper blocks → ${innerBlocks.length} blocks`
        );
        current = innerBlocks;
        continue; // Check if there's another layer
      }
    } catch {
      // Not valid JSON — return current level
    }

    return current;
  }

  return current;
}

/**
 * Parse content based on format — handles both markdown and BlockNote JSON.
 * For large documents (>50KB), uses the fast line-by-line parser to avoid
 * main-thread blocking from BlockNote's tryParseMarkdownToBlocks().
 */
export async function parseContent(
  editor: BlockNoteEditorCore,
  content: string,
  formatHint?: string
): Promise<PartialBlockType[]> {
  // Check if content is BlockNote JSON format
  if (formatHint === 'blocknote' || isBlockNoteJSON(content)) {
    try {
      let blocks = JSON.parse(content.trim());
      if (Array.isArray(blocks) && blocks.length > 0) {
        // Detect double-wrapped content: 1-2 paragraph blocks whose text IS valid
        // BlockNote JSON. This happens when blocks were serialized as a JSON string
        // and then saved as text content within paragraph blocks.
        blocks = unwrapDoubleEncodedBlocks(blocks);
        return decodeBlockEntities(migrateAsciiArtBlocks(migrateMermaidBlocks(blocks)));
      }
    } catch (error) {
      console.warn('Failed to parse BlockNote JSON, falling back to markdown:', error);
    }
  }

  // Sanitize markdown to remove junk lines from blocksToMarkdownLossy()
  const cleaned = sanitizeMarkdown(content);
  console.log(`[MarthaEditor] Sanitized: ${(content.length / 1024).toFixed(0)}KB → ${(cleaned.length / 1024).toFixed(0)}KB`);

  // For large markdown documents, use the fast parser to avoid blocking the main thread
  if (cleaned.length > FAST_PARSE_THRESHOLD) {
    console.log(`[MarthaEditor] Using fast parser for large document (${(cleaned.length / 1024).toFixed(0)}KB)`);
    return decodeBlockEntities(fastParseMarkdown(cleaned));
  }

  // Default: parse as markdown via BlockNote (fine for small documents)
  try {
    const blocks = await editor.tryParseMarkdownToBlocks(cleaned);
    // Sanity check: if BlockNote produced an unreasonable number of blocks
    // relative to content size, or zero blocks, fall back to fast parser.
    // This catches cases where the markdown→HTML→ProseMirror pipeline
    // misparses tables (e.g. `|` in code blocks treated as table delimiters).
    if (blocks.length === 0 || blocks.length > cleaned.length / 2) {
      console.warn(
        `[MarthaEditor] tryParseMarkdownToBlocks produced ${blocks.length} blocks for ${cleaned.length} chars — falling back to fast parser`
      );
      return decodeBlockEntities(fastParseMarkdown(cleaned));
    }
    return decodeBlockEntities(migrateAsciiArtBlocks(migrateMermaidBlocks(blocks)));
  } catch (error) {
    console.warn('[MarthaEditor] tryParseMarkdownToBlocks failed, falling back to fast parser:', error);
    return decodeBlockEntities(fastParseMarkdown(cleaned));
  }
}
